/// <reference path="../../types.d.ts" />

import React, { useEffect, useMemo } from 'react';
import {
  ChakraProvider,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
} from '@chakra-ui/react';
import { automationDashboardTheme } from '../../theme';
import {
  AutomationResultsChart,
  toAutomationResultsChartProps
} from '../../components/automation-results-chart';
import {
  AutomationTrendChart,
  DateRangeSelection,
  toAutomationTrendChartProps
} from '../../components/automation-trend-chart';
import {
  InsightsCard,
  FindingsCard,
  toInsightsPanelProps
} from '../../components/insights-panel';
import {
  VolumeByTypeChart,
  toVolumeByTypeChartProps
} from '../../components/volume-by-type-chart';
import { DatePicker, DatePickerPreset } from '../../components/date-picker/datepicker';
import {
  FilterOptionSource,
  healthSystemItems,
  selectionLabel,
  serviceLineItems,
  tenantItems
} from './filter-option-items';
import {
  DATE_RANGE_PRESETS,
  formatPillLabel,
  presetToRange
} from './date-presets';
import './automation-dashboard.scss';

export interface AutomationDashboardViewProps {
  healthSystem: string;
  serviceLine: string;
  /** The selected tenant (site) code; empty means all sites. */
  tenant?: string;
  dateRangePresetId: string;
  dateRange: { dos_start: string; dos_end: string };
  loadState: CMx.Loading;
  data: CMx.AutomationDashboardSummary | null;
  error: string | null;
  /** What the service says this caller may filter on; `null` until it has answered. */
  filterOptions?: FilterOptionSource | null;
  onMount: () => void;
  onHealthSystemChange: (code: string) => void;
  onServiceLineChange: (code: string) => void;
  onTenantChange?: (code: string) => void;
  onDateRangeChange: (selection: DateRangeSelection) => void;
}

interface PanelShellProps {
  loading: boolean;
  error: string | null;
  hasData: boolean;
  label: string;
  children: React.ReactNode;
}

const PanelShell: React.FC<PanelShellProps> = ({
  loading,
  error,
  hasData,
  label,
  children
}) => {
  if (error) {
    return (
      <div
        className="automation-dashboard__panel-error"
        role="alert"
        aria-label={`${label} failed to load`}>
        Failed to load {label.toLowerCase()}.
      </div>
    );
  }
  if (loading || !hasData) {
    return (
      <div
        className="automation-dashboard__panel-skeleton"
        aria-busy="true"
        aria-label={`Loading ${label.toLowerCase()}`}
      />
    );
  }
  return <>{children}</>;
};

const isoToDate = (iso: string): Date => new Date(`${iso}T00:00:00Z`);

const dateToIso = (d: Date): string => {
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const optionItemStyles = {
  unselected: {
    marginLeft: '0px',
    color: '#666666'
  },
  selected: {
    marginLeft: '0px',
    color: '#7055B3',
    backgroundColor: '#F5F3FF'
  }
};

/** Single filter pill — wraps Popover when popoverContent is provided, else plain button. */
interface FilterPillProps {
  icon?: React.ReactNode;
  label?: string;
  value: string;
  active?: boolean;
  ariaLabel?: string;
  onClick?: () => void;
  popoverContent?: React.ReactNode;
  /** Shows the value as a plain label: no dropdown, no chevron, not focusable. */
  readOnly?: boolean;
  'data-testid'?: string;
}

const FilterPill: React.FC<FilterPillProps> = ({
  icon,
  label,
  value,
  active = false,
  ariaLabel,
  popoverContent,
  readOnly = false,
  'data-testid': testId,
}) => {
  const pillClass = [
    'automation-dashboard__filter-pill',
    active ? 'automation-dashboard__filter-pill--active' : '',
    readOnly ? 'automation-dashboard__filter-pill--static' : '',
  ].filter(Boolean).join(' ');

  const pillContent = (
    <>
      {icon && (
        <span className="automation-dashboard__filter-pill-icon" aria-hidden="true">
          {icon}
        </span>
      )}
      {label && (
        <span className="automation-dashboard__filter-pill-label">{label}</span>
      )}
      <span className="automation-dashboard__filter-pill-value">{value}</span>
      {!readOnly && (
        <span className="automation-dashboard__filter-pill-chevron" aria-hidden="true">▼</span>
      )}
    </>
  );

  if (readOnly) {
    return (
      <span className={pillClass} data-testid={testId}>
        {pillContent}
      </span>
    );
  }

  if (popoverContent) {
    return (
      <Popover placement="bottom-end" isLazy>
        <PopoverTrigger>
          <button
            type="button"
            className={pillClass}
            aria-label={ariaLabel}
            data-testid={testId}>
            {pillContent}
          </button>
        </PopoverTrigger>
        <Portal>
          <PopoverContent width="auto">
            <PopoverBody>{popoverContent}</PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>
    );
  }

  return (
    <button
      type="button"
      className={pillClass}
      aria-label={ariaLabel}
      data-testid={testId}>
      {pillContent}
    </button>
  );
};

export const AutomationDashboard: React.FC<AutomationDashboardViewProps> = ({
  healthSystem,
  serviceLine,
  tenant = '',
  dateRangePresetId,
  dateRange,
  loadState,
  data,
  error,
  filterOptions,
  onMount,
  onHealthSystemChange,
  onServiceLineChange,
  onTenantChange,
  onDateRangeChange
}) => {
  useEffect(() => {
    onMount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loading = loadState.loading;
  const hasData = !!data;

  const datePickerPresets = useMemo<DatePickerPreset[]>(
    () =>
      DATE_RANGE_PRESETS.map((p) => {
        const range = presetToRange(p.id);
        return {
          id: p.id,
          label: p.label,
          startDate: new Date(`${range.dos_start}T00:00:00Z`),
          endDate: new Date(`${range.dos_end}T00:00:00Z`)
        };
      }),
    []
  );

  const dateRangePillLabel = formatPillLabel(dateRangePresetId, dateRange);

  const insightsProps = data ? toInsightsPanelProps(data) : null;
  const trendShapeProps = data ? toAutomationTrendChartProps(data) : null;
  const volumeProps = data ? toVolumeByTypeChartProps(data) : null;
  const resultsProps = data ? toAutomationResultsChartProps(data) : null;

  const handleDatePickerSelection = (event: CustomEvent<any>) => {
    const detail = event.detail?.value;
    if (!detail) return;
    const selection = detail.range?.[0];
    if (!selection?.startDate || !selection?.endDate) return;
    onDateRangeChange({
      presetId: detail.presetId ?? '',
      dos_start: dateToIso(selection.startDate),
      dos_end: dateToIso(selection.endDate)
    });
  };

  const healthSystemOptions = healthSystemItems(filterOptions);
  const serviceLineOptions = serviceLineItems(filterOptions);
  const hsLabel = selectionLabel(healthSystemOptions, healthSystem, 'All');
  const slLabel = selectionLabel(serviceLineOptions, serviceLine, 'All');
  const tenantOptions = tenantItems(filterOptions);
  const tenantLabel = selectionLabel(tenantOptions, tenant, 'All Sites');

  const headerControls = (
    <div className="automation-dashboard__header-controls" role="group" aria-label="Dashboard filters">
      {/* Date range pill */}
      <FilterPill
        icon="&#128197;"
        value={dateRangePillLabel}
        ariaLabel="Change date range"
        data-testid="dashboard-date-pill"
        popoverContent={
          <DatePicker
            startDate={isoToDate(dateRange.dos_start)}
            endDate={isoToDate(dateRange.dos_end)}
            presets={datePickerPresets}
            selectedPresetId={dateRangePresetId}
            onSelection={handleDatePickerSelection}
          />
        }
      />

      {/* Health system pill */}
      <FilterPill
        label="HS"
        value={hsLabel}
        ariaLabel="Change health system"
        readOnly={healthSystemOptions.length <= 1}
        data-testid="dashboard-hs-filter"
        popoverContent={
          <div style={{ display: 'flex', flexDirection: 'column' }} data-testid="dashboard-hs-select">
            {healthSystemOptions.map((item) => (
              <button
                key={item.value}
                type="button"
                style={{
                  ...optionItemStyles[item.value === healthSystem ? 'selected' : 'unselected'],
                  minHeight: '30px',
                  fontSize: '14.5px',
                  padding: '8px 16px',
                  border: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  width: '100%',
                  transition: 'background-color 0.2s'
                }}
                value={item.value}
                onClick={() => {
                  onHealthSystemChange(item.value as string);
                }}
                data-testid={item.value?.toString().trim()}>
                {item.label}
              </button>
            ))}
          </div>
        }
      />

      {/* Service line pill */}
      <FilterPill
        label="SL"
        value={slLabel}
        ariaLabel="Change service line"
        readOnly={serviceLineOptions.length <= 1}
        data-testid="dashboard-sl-filter"
        popoverContent={
          <div style={{ display: 'flex', flexDirection: 'column' }} data-testid="dashboard-sl-select">
            {serviceLineOptions.map((item) => (
              <button
                key={item.value}
                type="button"
                style={{
                  ...optionItemStyles[item.value === serviceLine ? 'selected' : 'unselected'],
                  minHeight: '30px',
                  fontSize: '14.5px',
                  padding: '8px 16px',
                  border: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  width: '100%',
                  transition: 'background-color 0.2s'
                }}
                value={item.value}
                onClick={() => {
                  onServiceLineChange(item.value as string);
                }}
                data-testid={item.value?.toString().trim()}>
                {item.label}
              </button>
            ))}
          </div>
        }
      />

      {/* Site (tenant) pill: shown whenever the caller has a site; a dropdown only when there are several */}
      {tenantOptions.length > 0 && (
        <FilterPill
          label="SITE"
          value={tenantLabel}
          ariaLabel="Change site"
          readOnly={tenantOptions.length <= 1}
          data-testid="dashboard-tenant-filter"
          popoverContent={
            <div style={{ display: 'flex', flexDirection: 'column' }} data-testid="dashboard-tenant-select">
              {tenantOptions.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  style={{
                    ...optionItemStyles[item.value === tenant ? 'selected' : 'unselected'],
                    minHeight: '30px',
                    fontSize: '14.5px',
                    padding: '8px 16px',
                    border: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    width: '100%',
                    transition: 'background-color 0.2s'
                  }}
                  value={item.value}
                  onClick={() => {
                    onTenantChange?.(item.value as string);
                  }}
                  data-testid={`tenant-${item.value?.toString().trim()}`}>
                  {item.label}
                </button>
              ))}
            </div>
          }
        />
      )}

      {/* Export — stub, no endpoint yet */}
      <button
        type="button"
        className="automation-dashboard__export-btn"
        aria-label="Export dashboard data (coming soon)"
        disabled
        title="Export coming soon">
        <span className="automation-dashboard__export-btn-icon" aria-hidden="true">&#8595;</span>
        Export
      </button>
    </div>
  );

  return (
    <ChakraProvider theme={automationDashboardTheme}>
      <section
        className="automation-dashboard"
        aria-label="Automation Dashboard">
        <header className="automation-dashboard__header">
          <div className="automation-dashboard__header-left">
            <div className="automation-dashboard__eyebrow">CMX Insights™</div>
            <h1 className="automation-dashboard__title">Automation Dashboard</h1>
            <div className="automation-dashboard__date-range">
              {dateRange.dos_start} &rarr; {dateRange.dos_end}
            </div>
          </div>
          {headerControls}
        </header>

        <div className="automation-dashboard__grid">
          {/* Left sidebar */}
          <div className="automation-dashboard__sidebar">
            <PanelShell loading={loading} error={error} hasData={hasData} label="Insights">
              {insightsProps && <InsightsCard {...insightsProps} />}
            </PanelShell>
            <PanelShell loading={loading} error={error} hasData={hasData} label="Findings">
              {insightsProps && <FindingsCard findings={insightsProps.findings} />}
            </PanelShell>
          </div>

          {/* Right main column */}
          <div className="automation-dashboard__main">
            <PanelShell loading={loading} error={error} hasData={hasData} label="Automation Trend">
              {trendShapeProps && (
                <AutomationTrendChart
                  {...trendShapeProps}
                  showFilters={false}
                />
              )}
            </PanelShell>

            <div className="automation-dashboard__bottom-row">
              <PanelShell loading={loading} error={error} hasData={hasData} label="Volume by Type">
                {volumeProps && <VolumeByTypeChart {...volumeProps} />}
              </PanelShell>
              <PanelShell loading={loading} error={error} hasData={hasData} label="Automation Results">
                {resultsProps && <AutomationResultsChart {...resultsProps} />}
              </PanelShell>
            </div>
          </div>
        </div>
      </section>
    </ChakraProvider>
  );
};

export default AutomationDashboard;
